import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import styles from "../../styles/pages/Other/OtherPage.module.css";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { AddAffiliate } from "../../components/Modal/Affiliate/AddAffiliate";
import { EditAffiliate } from "../../components/Modal/Affiliate/EditAffiliate";
import { AffiliatorLeadFailuresModal } from '../../components/Modal/Affiliate/AffiliatorLeadFailuresModal';
import { Title } from "../../components/UI/Custom/Title";
import { getAffiliate } from '../../utils/api/Affiliate/getAffiliateApi';
import { deleteAffiliate } from '../../utils/api/Affiliate/affiliateDeleteApi';
import { LoadingCircle } from '../../components/UI/Custom/LoadingCircle';
import { handleError } from '../../utils/custom/errorHandler';
import { Notification } from '../../components/UI/Notifications';
import { getMyAccountApi } from '../../utils/api/Account/getMyAccountApi';
import { LangContext } from '../../context/LangContext';
import { ThemeContext } from '../../context/ThemeContext';
import { ConfirmationModal } from '../../components/UI/Custom/ConfirmationModal';

const translations = {
    ru: {
        accessDenied: "Доступ на просмотр отсутствует",
        title: "Управление аффилиатами",
        addAffiliate: "Добавить Аффилиата",
        leadFailures: "Ошибки лидов",
        tableHeaders: {
            id: "ID",
            fullName: "ФИО",
            affiliate: "Аффилиат",
            email: "Email",
            actions: "Действия"
        },
        noAffiliatesFound: "Аффилиаты не найдены.",
        pagination: {
            prev: "Пред.",
            next: "След."
        },
        edit: "Изменить",
        delete: "Удалить",
        confirmTitle: "Подтвердите удаление",
        confirmMessage: (id) => `Вы уверены, что хотите безвозвратно удалить аффилиата #${id}?`,
        confirmText: "Да, удалить",
        cancelText: "Отмена",
        deleting: "Удаление..."
    },
    en: {
        accessDenied: "View access denied",
        title: "Affiliate Management",
        addAffiliate: "Add Affiliate",
        leadFailures: "Lead Failures",
        tableHeaders: {
            id: "ID",
            fullName: "Full Name",
            affiliate: "Affiliate",
            email: "Email",
            actions: "Actions"
        },
        noAffiliatesFound: "No affiliates found.",
        pagination: {
            prev: "Prev.",
            next: "Next."
        },
        edit: "Edit",
        delete: "Delete",
        confirmTitle: "Confirm Deletion",
        confirmMessage: (id) => `Are you sure you want to permanently delete Affiliate #${id}?`,
        confirmText: "Yes, delete",
        cancelText: "Cancel",
        deleting: "Deleting..."
    },

};

export const OtherPage = () => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;

    const [isAction, setIsAction] = useState({
        addAffiliate: false,
        editAffiliate: false,
        deleteAffiliate: false,
        leadFailures: false,
    });
    const [affiliateID, setAffiliateID] = useState(null);
    const [affiliates, setAffiliates] = useState([]);
    const [affiliateInfo, setAffiliateInfo] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState({});

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [affiliateToDeleteId, setAffiliateToDeleteId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [customerAccountPermissions, setCustomerAccountPermissions] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isPermissionsLoading, setIsPermissionsLoading] = useState(true);

    const getValue = (key) => affiliateInfo[key] || "";

    const handleDropdownChange = (filterName, value) => {
        setAffiliateInfo({ ...affiliateInfo, [filterName]: value });
    };

    useEffect(() => {
        const fetchAccountPermissions = async () => {
            setIsPermissionsLoading(true);
            try {
                const accountData = await getMyAccountApi();
                setIsSuperAdmin(accountData?.role?.name === 'Super Admin');
                if (accountData && accountData.permissions) {
                    const permissionsObject = accountData.permissions.reduce((acc, permission) => {
                        acc[permission.name] = permission;
                        return acc;
                    }, {});

                    setCustomerAccountPermissions(permissionsObject);
                } else {
                    setCustomerAccountPermissions({});
                }
            } catch (error) {
                handleError(error.response?.data || error, setErrors);
                setCustomerAccountPermissions({});
                setIsSuperAdmin(false);
            } finally {
                setIsPermissionsLoading(false);
            }
        };

        fetchAccountPermissions();
    }, []);

    const loadAffiliates = useCallback(async () => {
        if (!customerAccountPermissions || !customerAccountPermissions['user.list_affiliator_accounts']) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setErrors({});
        try {
            const { data, meta } = await getAffiliate(currentPage, itemsPerPage);
            if (Array.isArray(data)) {
                setAffiliates(data);
                setTotalPages(meta.total_pages || 1);
            } else {
                setAffiliates([]);
                setTotalPages(1);
            }
        } catch (error) {
            handleError(error.response?.data || error, setErrors);
            setAffiliates([]);
            setTotalPages(1);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, itemsPerPage, customerAccountPermissions]);

    useEffect(() => {
        if (!isPermissionsLoading && customerAccountPermissions && customerAccountPermissions['user.list_affiliator_accounts']) {
            loadAffiliates();
        } else if (!isPermissionsLoading && customerAccountPermissions && !customerAccountPermissions['user.list_affiliator_accounts']) {
            setIsLoading(false);
        }
    }, [isPermissionsLoading, customerAccountPermissions, loadAffiliates]);

    const handleAddAffiliate = () => {
        setAffiliateInfo({});
        setErrors({});
        setIsAction(prevState => ({
            ...prevState,
            addAffiliate: !prevState.addAffiliate
        }));
    };

    const handleEditAffiliateToggle = () => {
        setErrors({});
        setIsAction(prevState => ({
            ...prevState,
            editAffiliate: !prevState.editAffiliate
        }));
    };

    const reloadAffiliates = useCallback(async () => {
        await loadAffiliates();
    }, [loadAffiliates]);

    const affiliateMap = useMemo(() => {
        const map = {};
        if (Array.isArray(affiliates)) {
            affiliates.forEach((a) => {
                map[a.id] = a;
            });
        }
        return map;
    }, [affiliates]);

    const handleDeleteClick = (id) => {
        setErrors({});
        setAffiliateToDeleteId(id);
        setIsDeleteConfirmOpen(true);
    };

    const handleCloseDeleteConfirm = () => {
        setIsDeleteConfirmOpen(false);
        setAffiliateToDeleteId(null);
    };

    const confirmDelete = async () => {
        if (!affiliateToDeleteId) return;

        setIsDeleting(true);
        setErrors({});

        try {
            await deleteAffiliate(affiliateToDeleteId);
            await loadAffiliates();

        } catch (error) {
            handleError(error.response?.data || error, setErrors);
        } finally {
            setIsDeleting(false);
            setAffiliateToDeleteId(null);
            setIsDeleteConfirmOpen(false);
        }
    };


    const handlePreviousPage = () => {
        setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prevPage => Math.min(prevPage + 1, totalPages));
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderPaginationButtons = () => {
        const pages = [];
        const maxPageButtons = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

        if (endPage - startPage + 1 < maxPageButtons) {
            startPage = Math.max(1, endPage - maxPageButtons + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={`${styles.pageButton} ${i === currentPage ? styles.activePage : ''} ${theme === 'dark' ? styles.darkPageButton : ''} ${i === currentPage && theme === 'dark' ? styles.darkActivePage : ''}`}
                    onClick={() => handlePageClick(i)}
                    disabled={isLoading}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    const containerClass = theme === 'dark' ? `${styles.container} ${styles.darkContainer}` : styles.container;
    const affiliateCardClass = theme === 'dark' ? `${styles.affiliateCard} ${styles.darkAffiliateCard}` : styles.affiliateCard;
    const affiliateTableClass = theme === 'dark' ? `${styles.affiliateTable} ${styles.darkAffiliateTable}` : styles.affiliateTable;
    const tableHeadClass = theme === 'dark' ? `${styles.affiliateTableHead} ${styles.darkAffiliateTableHead}` : styles.affiliateTableHead;
    const tableRowClass = theme === 'dark' ? `${styles.affiliateTableRow} ${styles.darkAffiliateTableRow}` : styles.affiliateTableRow;
    const tableCellClass = theme === 'dark' ? `${styles.affiliateTableCell} ${styles.darkAffiliateTableCell}` : styles.affiliateTableCell;
    const addButtonClass = theme === 'dark' ? `${styles.addButton} ${styles.darkAddButton}` : styles.addButton;
    const editButtonClass = theme === 'dark' ? `${styles.editButton} ${styles.darkEditButton}` : styles.editButton;
    const deleteButtonClass = theme === 'dark' ? `${styles.deleteButton} ${styles.darkDeleteButton}` : styles.deleteButton;
    const noDataMessageClass = theme === 'dark' ? `${styles.noDataMessage} ${styles.darkNoDataMessage}` : styles.noDataMessage;
    const loadingOverlayClass = theme === 'dark' ? `${styles.loadingOverlay} ${styles.darkLoadingOverlay}` : styles.loadingOverlay;


    if (isPermissionsLoading) {
        return (
            <div className={loadingOverlayClass}>
                <LoadingCircle size={40} thickness={3} />
            </div>
        );
    }

    if (!customerAccountPermissions || !customerAccountPermissions['user.list_affiliator_accounts']) {
        return (
            <div className={containerClass} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', fontSize: '24px', color: '#888' }}>
                {t.accessDenied}
            </div>
        );
    }

    return (
        <div className={containerClass}>
            <Title h2={t.title} activeSpan={false} theme={theme} />
            {errors.general && <Notification message={errors.general} type={"error"} />}
            <div className={affiliateCardClass}>
                <div className={styles.cardHeader}>
                    {isSuperAdmin && customerAccountPermissions['lead.list_lead_logs'] && (
                        <button
                            className={addButtonClass}
                            onClick={() => setIsAction((prevState) => ({ ...prevState, leadFailures: true }))}
                        >
                            <ErrorOutlineIcon style={{ fontSize: '1.1rem', marginRight: '6px' }} />
                            {t.leadFailures}
                        </button>
                    )}
                    {customerAccountPermissions['user.create_affiliator_accounts'] && (
                        <button className={addButtonClass} onClick={handleAddAffiliate}>
                            {t.addAffiliate}
                        </button>
                    )}
                </div>
                {isLoading ? (
                    <div className={loadingOverlayClass}>
                        <LoadingCircle size={40} thickness={3} />
                    </div>
                ) : (
                    <div className={styles.tableWrapper}>
                        <table className={affiliateTableClass}>
                            <thead className={tableHeadClass}>
                                <tr>
                                    <th>{t.tableHeaders.id}</th>
                                    <th>{t.tableHeaders.fullName}</th>
                                    <th>{t.tableHeaders.affiliate}</th>
                                    <th>{t.tableHeaders.email}</th>
                                    <th>{t.tableHeaders.actions}</th>
                                </tr>
                            </thead>
                            <tbody className={styles.affiliateTableBody}>
                                {Array.isArray(affiliates) && affiliates.length > 0 ? (
                                    affiliates.map((affiliate) => (
                                        <tr key={affiliate.id} className={tableRowClass}>
                                            <td className={tableCellClass}>#{affiliate.id}</td>
                                            <td className={tableCellClass}>
                                                {affiliate.first_name || affiliate.last_name ? (
                                                    `${affiliate.first_name || ''} ${affiliate.last_name || ''}`.trim()
                                                ) : (
                                                    "N/A"
                                                )}
                                            </td>
                                            <td className={tableCellClass}>{affiliate.affiliate}</td>
                                            <td className={tableCellClass}>{affiliate.email}</td>
                                            <td className={tableCellClass}>
                                                <div className={styles.actions}>
                                                    {customerAccountPermissions['user.update_affiliator_accounts'] && (
                                                        <button
                                                            className={editButtonClass}
                                                            onClick={() => {
                                                                handleEditAffiliateToggle();
                                                                setAffiliateID(affiliate.id);
                                                            }}
                                                        >
                                                            <EditIcon style={{ fontSize: '1.1rem', marginRight: '5px' }} />
                                                            {t.edit}
                                                        </button>
                                                    )}
                                                    {customerAccountPermissions['user.delete_affiliator_accounts'] && (
                                                        <button
                                                            className={deleteButtonClass}
                                                            onClick={() => handleDeleteClick(affiliate.id)}
                                                        >
                                                            <DeleteIcon style={{ fontSize: '1.1rem', marginRight: '5px' }} />
                                                            {t.delete}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className={noDataMessageClass}>
                                            {t.noAffiliatesFound}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {!isLoading && totalPages > 1 && (
                    <div className={styles.pagination}>
                        <button
                            className={`${styles.pageButton} ${theme === 'dark' ? styles.darkPageButton : ''}`}
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1 || isLoading}
                        >
                            {t.pagination.prev}
                        </button>
                        {renderPaginationButtons()}
                        <button
                            className={`${styles.pageButton} ${theme === 'dark' ? styles.darkPageButton : ''}`}
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages || isLoading}
                        >
                            {t.pagination.next}
                        </button>
                    </div>
                )}
            </div>

            {isAction.addAffiliate && (
                <AddAffiliate
                    handleAddAffiliate={handleAddAffiliate}
                    handleDropdownChange={handleDropdownChange}
                    affiliateInfo={affiliateInfo}
                    getValue={getValue}
                    fetchAffiliates={reloadAffiliates}
                />
            )}

            {isAction.editAffiliate && affiliateID && (
                <EditAffiliate
                    handleEditAffiliate={handleEditAffiliateToggle}
                    data={affiliateMap[affiliateID]}
                    handleDropdownChange={handleDropdownChange}
                    affiliateInfo={affiliateInfo}
                    getValue={getValue}
                    fetchAffiliates={reloadAffiliates}
                />
            )}

            {isAction.leadFailures && (
                <AffiliatorLeadFailuresModal
                    onClose={() => setIsAction((prevState) => ({ ...prevState, leadFailures: false }))}
                />
            )}

            {isDeleteConfirmOpen && (
                <ConfirmationModal
                    isOpen={isDeleteConfirmOpen}
                    onClose={handleCloseDeleteConfirm}
                    onConfirm={confirmDelete}
                    title={t.confirmTitle}
                    message={t.confirmMessage(affiliateToDeleteId)}
                    confirmText={isDeleting ? t.deleting : t.confirmText}
                    cancelText={t.cancelText}
                    theme={theme}
                />
            )}
        </div>
    );
};
